import { useFormikContext } from 'formik'
import React, { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { PopoverInput } from '@ui/components/Popover'
import { Radio } from '@ui/components/Radio'

import { isOne } from '@utils/validations'

import { AccountDetailedDto } from '@model/AccountDetailedDto'
import { NameDto } from '@model/NameDto'

import { createFormField } from '@global/formField/FormField'

import {
  CampaignExtendedNameDto,
  CreateTradingAccountCampaignFormValues,
} from './CreateTradingAccountCampaign.types'
import { GenericReferralType } from './CreateTradingAccountCampaign.utils'

const FormField = createFormField<CreateTradingAccountCampaignFormValues>()

export interface IBCodeFieldProps {
  onChange(): void
  apiError?: string
}

export const IBCodeField: React.FC<IBCodeFieldProps> = ({ onChange, apiError }) => {
  const { t } = useTranslation()
  const { values, setFieldValue, setFieldError } =
    useFormikContext<CreateTradingAccountCampaignFormValues>()

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.value) setFieldError('ibCode', '')
    onChange()
    setFieldValue('ibCode', event.target.value)
  }

  const referralType = values.ibState?.id?.toString()

  const isExistingReferral =
    referralType !== GenericReferralType.None && referralType !== GenericReferralType.New

  if (referralType === GenericReferralType.None) {
    return null
  }

  return (
    <FormField
      error={apiError}
      name='ibCode'
      label={t('Trading Account.Referral Code')}
      required
      disabled={isExistingReferral}
      onChange={handleChange}
    />
  )
}

export interface NameFieldProps {
  data: CampaignExtendedNameDto[]
  allFieldsDisabled: boolean
  campaignId: string | null
  calculationType: NameDto | undefined
  campaignType: NameDto | undefined
  isCampaign?: boolean
  onSelect(campaign: CampaignExtendedNameDto): void
}

export const NameField: React.FC<NameFieldProps> = ({
  data,
  campaignId,
  allFieldsDisabled,
  isCampaign,
  onSelect,
}) => {
  const { t } = useTranslation()
  const { values } = useFormikContext<CreateTradingAccountCampaignFormValues>()
  const getHintText = () => data.find((c) => c.id === campaignId)?.campaignDescription || ''
  return (
    <PopoverInput
      name='tradingAccount.name'
      label={t('Trading Account.Trading Account Type')}
      value={values.tradingAccount?.id}
      options={data.filter((campaign) => (isCampaign ? campaign.id !== '0' : true))}
      setValueOnSelect={false}
      disabled={allFieldsDisabled || isOne(data.length)}
      required
      onChange={onSelect}
      hint={getHintText()}
    />
  )
}

export interface ReferralFieldProps {
  referral: NameDto<string>
  account: AccountDetailedDto | undefined
  isValidatingIBCode: boolean
  setAllFieldsDisabled(value: boolean): void
}

export const ReferralField: React.FC<ReferralFieldProps> = ({
  account,
  referral,
  isValidatingIBCode,
  setAllFieldsDisabled,
}) => {
  const { t } = useTranslation()
  const { values, setFieldValue, setFieldError } =
    useFormikContext<CreateTradingAccountCampaignFormValues>()

  const handleChange = (value: string) => {
    const { campaignId, tradingAccount } = { ...values }
    setFieldValue('campaignId', campaignId)
    setFieldValue('tradingAccount', tradingAccount)

    const isNewReferral = value === GenericReferralType.New
    const isNoReferral = value === GenericReferralType.None
    const isExistingReferral = !isNewReferral && !isNoReferral

    if (isExistingReferral) {
      setFieldValue('ibState', referral)
      setFieldValue('ibCode', values.initialIbCode)
      setFieldError('ibCode', '')
      setFieldValue('introducingBroker', account?.introducingBroker)
      setFieldValue('referralOptionId', referral.id)
      setAllFieldsDisabled(false)
    } else if (isNewReferral) {
      setFieldValue('ibState', { id: GenericReferralType.New, name: t('New Referral') })
      setAllFieldsDisabled(true)
      setFieldValue('ibCode', '')
      setFieldValue('introducingBroker', undefined)
      setFieldError('ibCode', '')
      setFieldValue('referralOptionId', GenericReferralType.New)
    } else {
      setFieldValue('ibState', referral)
      setAllFieldsDisabled(false)
      setFieldValue('ibCode', '')
      setFieldError('ibCode', '')
      setFieldValue('introducingBroker', undefined)
      setFieldValue('referralOptionId', GenericReferralType.None)
    }
  }

  return (
    <Radio
      name={referral.id}
      value={referral.id}
      checked={values?.ibState?.id === referral.id}
      disabled={isValidatingIBCode}
      onChange={handleChange}
    >
      {referral.name}
    </Radio>
  )
}
